 import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { aiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSpeechRecognitionContext } from
'../../contexts/SpeechRecognitionContext';
import MicrophoneButton from
'../common/MicrophoneButton/MicrophoneButton';
import GraphVisualization from './GraphVisualization';
import { generateAnalysisPDF } from './pdfExport';
import { buildAnalysisSummary } from './analysisSummary';
import './AIExplorerPage.css';

// =========================================
// Constants
// =========================================

/**
 * Flow states for progressive disclosure
 */
const FLOW_STATES = {
  IDLE: 'idle',
  EXTRACTING_TERMS: 'extracting_terms',
  PREVIEWING: 'previewing',
  // AWAITING_DECISION removed October 27, 2026 (Phase B.1 ruling 5): the
  // Recency / Attention / Balanced choice it waited on is gone.
  ANALYZING: 'analyzing',
  COMPLETE: 'complete',
  ERROR: 'error'
};

// =========================================
// Helper Functions
// =========================================

/**
 * Preprocesses markdown text to help remark-gfm parse tables correctly.
 * Ensures blank lines before and after tables.
 */
const preprocessMarkdown = (text) => {
  if (!text) return text;
  
  const lines = text.split('\n');
  const processed = [];
  let inTable = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isTableLine = line.trim().startsWith('|');
    
    if (isTableLine && !inTable) {
      if (processed.length > 0 && processed[processed.length - 1].trim() 
!== '') {
        processed.push('');
      }
      inTable = true;
    } else if (!isTableLine && inTable) {
      if (line.trim() !== '') {
        processed.push('');
      }
      inTable = false;
    }
    
    processed.push(line);
  }
  
  return processed.join('\n');
};

/**
 * Strip any AI-generated Sources section from the response text.
 * The frontend will render sources separately from citedEntries data.
 */
const stripSourcesSection = (text) => {
  if (!text) return text;

  // More aggressive patterns to catch all AI-generated source content
  const sourcesPatterns = [
    // Standard headers (with flexible whitespace)
    /\n+##?\s*SOURCES:?\s*\n[\s\S]*/i,
    /\n+\*\*SOURCES:?\*\*\s*\n[\s\S]*/i,
    /\n+SOURCES:?\s*\n[\s\S]*/i,
    /\n+##?\s*Sources\s*\n[\s\S]*/i,
    /\n+Sources\s+Cited:?\s*\n[\s\S]*/i,

    // FROM: patterns (AI's source grouping format)
    /\n+---\s*\n[\s\S]*FROM:/i,
    /\n+FROM:\s*\[?[^\]\n]+\]?\s*(?:\([^)]*\))?\s*You captured[\s\S]*/i,
    /\n+FROM:\s+[^\n]+\n[\s\S]*/i,

    // Catch broken table headers that indicate sources section
    /\n+\|\s*#\s*\|\s*(?:FOCUS|Focus|SOURCE|Source|TITLE|Title)[\s\S]*/i,

    // Catch any remaining pipe-table fragments after source headers
    /\n+\|\s*\[\d+\]\s*\|[\s\S]*/i
  ];

  let cleaned = text;
  for (const pattern of sourcesPatterns) {
    cleaned = cleaned.replace(pattern, '');
  }

  return cleaned.trim();
};

/**
 * Parse markdown text into sections for collapsible display.
 * Returns: { intro, bodySections, conclusion, rawText }
 */
const parseMarkdownSections = (text) => {
  if (!text) return { intro: '', bodySections: [], conclusion: '', rawText: text };

  const lines = text.split('\n');
  let intro = '';
  const bodySections = [];
  let conclusion = '';
  let currentSection = null;
  let inIntro = true;
  let foundFirstH2 = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const h2Match = line.match(/^##\s+(.+)$/);

    if (h2Match) {
      foundFirstH2 = true;
      const heading = h2Match[1].trim();

      if (currentSection) {
        if (currentSection.isConclusion) {
          conclusion = currentSection.content;
        } else {
          bodySections.push(currentSection);
        }
      }

      const isConclusion = /conclusion/i.test(heading);
      currentSection = {
        headingText: heading,
        content: '',
        isConclusion: isConclusion
      };
      inIntro = false;
    } else {
      if (inIntro && !foundFirstH2) {
        intro += line + '\n';
      } else if (currentSection) {
        currentSection.content += line + '\n';
      }
    }
  }

  if (currentSection) {
    if (currentSection.isConclusion) {
      conclusion = currentSection.content;
    } else {
      bodySections.push(currentSection);
    }
  }

  if (bodySections.length === 0 && !conclusion) {
    return { intro: '', bodySections: [], conclusion: '', rawText: text };
  }

  return { intro: intro.trim(), bodySections, conclusion: conclusion.trim(), rawText: null };
};

// =========================================
// Main Component
// =========================================

const AIExplorerPage = () => {
  // =========================================
  // State Management
  // =========================================
  
  const [query, setQuery] = useState('');
  const [currentQuery, setCurrentQuery] = useState('');
  // Provider is fixed to Claude (Anthropic). The value still flows through to
  // the preview/analyze API calls — we just no longer expose a selector for it.
  const aiProvider = 'claude';
  const [flowState, setFlowState] = useState(FLOW_STATES.IDLE);
  const [error, setError] = useState(null);
  // The `searchTerms` wire value ([queryText]) and the preview's
  // `extractedTerms` are no longer held in state: since Phase B.1 (October 27,
  // 2026) the preview always proceeds straight to analysis, so both are passed
  // through handleQueryFlow → runAnalysis directly. They were state only for
  // the removed prioritization step, which resumed the flow later.
  // conceptTags: the top tags across the retrieved entries, returned by
  // /preview. Phase B.2 (November 3, 2026) replaced their chip display with
  // the searched-terms block; they still feed the Constellations view and the
  // PDF header.
  const [conceptTags, setConceptTags] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [response, setResponse] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [progressData, setProgressData] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());

  // Query usage tracking (freemium gating)
  const [queryUsage, setQueryUsage] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [activeTab, setActiveTab] = useState('progress');

  // Derived state
  const isProcessing = [
    FLOW_STATES.EXTRACTING_TERMS,
    FLOW_STATES.PREVIEWING,
    FLOW_STATES.ANALYZING
  ].includes(flowState);

  // =========================================
  // Refs
  // =========================================
  
  const queryTextareaRef = useRef(null);

  // =========================================
  // Speech Recognition
  // =========================================
  
  const {
    isEnabled: sttEnabled,
    isRecording,
    statusMessage: sttStatusMessage,
    stopRecording
  } = useSpeechRecognitionContext();

  // =========================================
  // Auth & Usage
  // =========================================

  const { hasSubscriberAccess } = useAuth();
  const hasQueryLimit = queryUsage?.limit != null;
  const queriesRemaining = hasQueryLimit ? queryUsage.limit - queryUsage.used : null;
  const limitReached = hasQueryLimit && queriesRemaining <= 0;
  const showQueryCounter = hasQueryLimit && !hasSubscriberAccess;

  // Fetch usage on mount
  useEffect(() => {
    aiService.getUsage().then(usage => {
      if (usage) setQueryUsage(usage);
    });
  }, []);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  // Auto-switch to Map tab when graph data arrives
  useEffect(() => {
    if (graphData && flowState === FLOW_STATES.COMPLETE) {
      setActiveTab('map');
    }
  }, [graphData, flowState]);

  const handleTranscriptChange = useCallback((text) => {
    setQuery(text);
  }, []);

  useEffect(() => {
    if (isProcessing && isRecording) {
      stopRecording();
    }
  }, [isProcessing, isRecording, stopRecording]);

  // =========================================
  // Flow Control Functions
  // =========================================

  const resetState = useCallback(() => {
    setError(null);
    setPreviewData(null);
    setResponse(null);
    setProgressData(null);
    setExpandedSections(new Set());
    setGraphData(null);
    setActiveTab('progress');
  }, []);

  const runAnalysis = useCallback(async (queryText, terms, carriedTerms = null) => {
    try {
      setFlowState(FLOW_STATES.ANALYZING);

      // No `strategy` (Phase B.1 ruling 5, October 27, 2026): the server
      // ignores it, and the budget guards trim in fused order.
      const analysisResult = await aiService.analyze(queryText, terms, {
        provider: aiProvider,
        queryType: 'explore',
        extractedTerms: carriedTerms
      });

      if (analysisResult.success) {
        setResponse(analysisResult);
        console.log('GRAPH DATA CHECK:', analysisResult.graphData ? 'present' : 'missing', Object.keys(analysisResult));
        if (analysisResult.graphData) {
          setGraphData(analysisResult.graphData);
        }
        setFlowState(FLOW_STATES.COMPLETE);

        // Update usage from response if present
        if (analysisResult.queryUsage) {
          setQueryUsage(analysisResult.queryUsage);

          // Warning toasts for freemium users approaching limit
          const { used, limit } = analysisResult.queryUsage;
          if (limit != null && !hasSubscriberAccess) {
            const remaining = limit - used;
            if (remaining === 2) {
              setToastMessage(`${remaining} queries remaining this week`);
            } else if (remaining === 1) {
              setToastMessage('Last query remaining this week!');
            }
          }
        }
      } else {
        throw new Error(analysisResult.error || 'Analysis failed');
      }
    } catch (err) {
      console.error('Analysis error:', err);

      // Handle 429 rate limit with usage data
      if (err.status === 429 && err.usage) {
        setQueryUsage({ ...err.usage, allowed: false });
        setError('You have reached your weekly query limit.');
      } else {
        setError(err.message);
      }

      setFlowState(FLOW_STATES.ERROR);
    }
  }, [aiProvider, hasSubscriberAccess]);

  const handleQueryFlow = useCallback(async (queryText) => {
    try {
      // `searchTerms` is still sent as `[queryText]` for wire compatibility,
      // but since Phase A (October 27, 2026) it is NOT a search input: the
      // backend extracts terms from the question during preview (tag
      // vocabulary, then Haiku, then a deterministic fallback) and returns them
      // as `extractedTerms`, which are carried to /analyze below. From May 3,
      // 2026 until Phase A the raw question WAS the keyword search term, and
      // the keyword leg returned zero on every run. The chip display is
      // hydrated from preview.conceptTags after retrieval.
      const effectiveTerms = [queryText];

      setFlowState(FLOW_STATES.PREVIEWING);

      const previewResult = await aiService.preview(
        queryText,
        effectiveTerms,
        aiProvider
      );

      if (!previewResult.success) {
        throw new Error(previewResult.error || 'Failed to preview results');
      }

      setPreviewData(previewResult);
      setConceptTags(previewResult.conceptTags || []);
      const carriedTerms = Array.isArray(previewResult.extractedTerms)
        ? previewResult.extractedTerms
        : null;

      // Step 3: always proceed (Phase B.1 ruling 5, October 27, 2026). An
      // over-budget preview used to stop here for a Recency / Attention /
      // Balanced choice that the server ignored, and the flow stalled when the
      // options failed to render. `exceedsBudget` is still on the preview for
      // the status line; the server's budget guards decide what is trimmed.
      await runAnalysis(queryText, effectiveTerms, carriedTerms);

    } catch (err) {
      console.error('Query flow error:', err);
      setError(err.message);
      setFlowState(FLOW_STATES.ERROR);
    }
  }, [aiProvider, runAnalysis]);

  // =========================================
  // Collapsible Section Handlers
  // =========================================

  const toggleSection = useCallback((sectionIndex) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionIndex)) {
        next.delete(sectionIndex);
      } else {
        next.add(sectionIndex);
      }
      return next;
    });
  }, []);

  const expandAllSections = useCallback((totalSections) => {
    setExpandedSections(new Set(Array.from({ length: totalSections }, (_, 
i) => i)));
  }, []);

  const collapseAllSections = useCallback(() => {
    setExpandedSections(new Set());
  }, []);

  // =========================================
  // Citation Click Handler
  // =========================================

  useEffect(() => {
    const handleCitationClick = (e) => {
      const link = e.target.closest('a');
      if (!link) return;
      
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('#source-')) return;
      
      e.preventDefault();
      
      const citationNum = href.replace('#source-', '');
      const targetRow = 
document.getElementById(`source-row-${citationNum}`);
      
      if (targetRow) {
        targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
        targetRow.classList.add('highlighted');
        setTimeout(() => {
          targetRow.classList.remove('highlighted');
        }, 2000);
      }
    };
    
    document.addEventListener('click', handleCitationClick);
    return () => document.removeEventListener('click', 
handleCitationClick);
  }, [response]);

  // =========================================
  // Event Handlers
  // =========================================

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!query.trim()) return;
    
    const queryText = query.trim();
    setCurrentQuery(queryText);
    setQuery('');
    resetState();
    
    await handleQueryFlow(queryText);
  };

  const handleKeyPress = (e) => {   
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // =========================================
  // Render Functions
  // =========================================

  /**
   * Render collapsible response with sections
   */
  const renderCollapsibleResponse = () => {
    if (!response) return null;

    const processedText =
preprocessMarkdown(stripSourcesSection(response.text));

    console.log('AI Explorer response:', {
      rawTextLength: response.text?.length,
      hasH2Headers: /^##\s+/m.test(response.text),
      processedTextLength: processedText?.length,
      firstH2Match: response.text?.match(/^##\s+(.+)$/m)?.[1]
    });

    const sections = parseMarkdownSections(processedText);

    // Phase B.2 (November 3, 2026): when themes were not formed, or no theme
    // bore on the question, the answer is one "What the entries say" section:
    // a status line goes above the narrative and no theme section headers are
    // shown (copy verbatim; B.2.1, November 3: the no-relevant case has its
    // own line).
    const noThemeSections = response.themesFormed === false || response.noRelevantThemes === true;

    console.log('Parsed sections:', {
      intro: sections.intro?.substring(0, 100),
      bodySectionsCount: sections.bodySections?.length,
      bodySectionHeadings: sections.bodySections?.map(s => s.headingText),
      conclusion: sections.conclusion?.substring(0, 100),
      hasRawText: !!sections.rawText
    });

    // If no sections found, render normally
    if (sections.rawText) {
      return (
        <div className="response-text">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({node, href, children, ...props}) => {
                const text = Array.isArray(children) ? children[0] : 
children;
                if (typeof text === 'string' && /^\d+$/.test(text)) {
                  return (
                    <a
                      href={`#source-${text}`}
                      className="citation-link"
                      {...props}
                    >
                      [{text}]
                    </a>
                  );
                }
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                    {children}
                  </a>
                );
              }
            }}
          >
            {sections.rawText}
          </ReactMarkdown>
        </div>
      );
    }

    if (noThemeSections) {
      const citationLink = ({node, href, children, ...props}) => {
        const text = Array.isArray(children) ? children[0] : children;
        if (typeof text === 'string' && /^\d+$/.test(text)) {
          return (
            <a href={`#source-${text}`} className="citation-link" {...props}>
              [{text}]
            </a>
          );
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
          </a>
        );
      };
      const body = sections.rawText
        ? sections.rawText
        : [sections.intro, ...sections.bodySections.map(sec => sec.content.trim()), sections.conclusion]
            .filter(Boolean)
            .join('\n\n');
      return (
        <div className="response-text">
          <p className="response-status-line">
            {response.noRelevantThemes === true
              ? 'None of the entries found bear directly on this question. What follows describes what they contain.'
              : 'Themes could not be formed for this question. What follows describes the entries found.'}
          </p>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: citationLink }}>
            {body}
          </ReactMarkdown>
        </div>
      );
    }

    // Render with collapsible sections
    const allExpanded = sections.bodySections.length > 0 &&
                        sections.bodySections.every((_, idx) => 
expandedSections.has(idx));

    return (
      <div className="response-text">
        {/* Introduction - always visible */}
        {sections.intro && (
          <div className="response-intro">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({node, href, children, ...props}) => {
                  const text = Array.isArray(children) ? children[0] : 
children;
                  if (typeof text === 'string' && /^\d+$/.test(text)) {
                    return (
                      <a href={`#source-${text}`} 
className="citation-link" {...props}>
                        [{text}]
                      </a>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                      {children}
                    </a>
                  );
                }
              }}
            >
              {sections.intro}
            </ReactMarkdown>
          </div>
        )}

        {/* Expand/Collapse All controls */}
        {sections.bodySections.length > 0 && (
          <div className="section-controls">
            {allExpanded ? (
              <button
                className="section-control-btn"
                onClick={collapseAllSections}
                aria-label="Collapse all sections"
              >
                Collapse All
              </button>
            ) : (
              <button
                className="section-control-btn"
                onClick={() => 
expandAllSections(sections.bodySections.length)}
                aria-label="Expand all sections"
              >
                Expand All
              </button>
            )}
          </div>
        )}

        {/* Collapsible body sections */}
        {sections.bodySections.map((section, index) => {
          const isExpanded = expandedSections.has(index);
          const sectionId = `section-${index}`;

          return (
            <div key={index} className="collapsible-section">
              <button
                className="section-heading"
                onClick={() => toggleSection(index)}
                aria-expanded={isExpanded}
                aria-controls={sectionId}
              >
                <span className={`chevron ${isExpanded ? 'expanded' : 
''}`}>▶</span>
                <h3>{section.headingText}</h3>
              </button>
              <div
                id={sectionId}
                className={`section-content ${isExpanded ? 'expanded' : 
'collapsed'}`}
                aria-hidden={!isExpanded}
              >
                <div className="section-content-inner">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({node, href, children, ...props}) => {
                        const text = Array.isArray(children) ? children[0] 
: children;
                        if (typeof text === 'string' && 
/^\d+$/.test(text)) {
                          return (
                            <a href={`#source-${text}`} 
className="citation-link" {...props}>
                              [{text}]
                            </a>
                          );
                        }
                        return (
                          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                            {children}
                          </a>
                        );
                      }
                    }}
                  >
                    {section.content.trim()}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          );
        })}

        {/* Conclusion - always visible */}
        {sections.conclusion && (
          <div className="response-conclusion">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({node, href, children, ...props}) => {
                  const text = Array.isArray(children) ? children[0] : 
children;
                  if (typeof text === 'string' && /^\d+$/.test(text)) {
                    return (
                      <a href={`#source-${text}`} 
className="citation-link" {...props}>
                        [{text}]
                      </a>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                      {children}
                    </a>
                  );
                }
              }}
            >
              {sections.conclusion}
            </ReactMarkdown>
          </div>
        )}
      </div>
    );
  };

  /**
   * Render the Sources Cited table from citedEntries data
   */
  const renderSourcesTable = () => {
    if (!response?.citedEntries || response.citedEntries.length === 0) {
      return null;
    }

    // Sort entries by citation number to ensure proper order
    const sortedEntries = [...response.citedEntries].sort((a, b) => 
      a.citationNumber - b.citationNumber
    );

    return (
      <div className="sources-cited-section">
        <h3 className="sources-cited-title">Sources Cited</h3>
        <table className="sources-cited-table">
          <thead>
            <tr>
              <th className="col-citation">#</th>
              <th className="col-title">Source</th>
              <th className="col-focus">Focus</th>
              <th className="col-thought">Thought</th>
            </tr>
          </thead>
          <tbody>
            {sortedEntries.map((entry) => (
              <tr 
                key={entry.citationNumber} 
                id={`source-row-${entry.citationNumber}`}
                className="source-row"
              >
                <td className="col-citation">
                  <span 
className="citation-number">[{entry.citationNumber}]</span>
                </td>
                <td className="col-title">
                  {entry.docUrl ? (
                    <a href={entry.docUrl} target="_blank" rel="noreferrer">
                      {entry.docTitle}
                    </a>
                  ) : (
                    <span>{entry.docTitle}</span>
                  )}
                </td>
                <td className="col-focus">{entry.focus}</td>
                <td className="col-thought">{entry.thought}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  /**
   * Searched-terms block (Phase B.2, ruled November 3, 2026; copy verbatim).
   * Replaces the concept-chip sentence and chips. From the preview's
   * `searchedTerms` (built from the extracted terms): the admitting terms,
   * each with where it matched among the retrieved entries (the "Strengthened
   * by" components line was removed at the November 3 close-out; components
   * stay in the staff report); then, for each demoted broad term, the coverage
   * line, and the "selected by meaning" sentence when the keyword leg was
   * demoted entirely.
   */
  const WHERE_LABELS = { tag: 'your tag', passage: 'in the passage', thoughts: 'in your Thoughts' };

  const renderSearchedTerms = () => {
    if (!previewData) return null;
    const admitting = previewData.searchedTerms?.admitting || [];
    const broadTerms = Array.isArray(previewData.broadTerms) ? previewData.broadTerms : [];
    const demotedEntirely = previewData.keywordLegDemotedEntirely === true;
    if (admitting.length === 0 && broadTerms.length === 0 && !demotedEntirely) {
      return null;
    }

    return (
      <div className="disclosure-section search-terms-section">
        {admitting.length > 0 && (
          <p className="disclosure-section-description searched-terms-line">
            Searched for: {admitting.map(t => (
              t.where && t.where.length
                ? `${t.text} (${t.where.map(w => WHERE_LABELS[w]).filter(Boolean).join(', ')})`
                : t.text
            )).join(' · ')}
          </p>
        )}
        {broadTerms.map((b, index) => {
          const pct = typeof b.share === 'number' ? Math.round(b.share * 100) : null;
          const narrower = Array.isArray(b.narrowerTags) ? b.narrowerTags.slice(0, 8) : [];
          return (
            <p key={index} className="disclosure-section-description searched-terms-line">
              "{b.term}" appears on {pct}% of your entries, so it wasn't used alone to select.
              {narrower.length > 0 && ` You often tag more specifically: ${narrower.join(', ')}.`}
            </p>
          );
        })}
        {demotedEntirely && (
          <p className="disclosure-section-description searched-terms-line">
            Entries were selected by meaning for this question.
          </p>
        )}
      </div>
    );
  };

  /**
   * Render the entries preview section
   */
  const renderEntriesPreview = () => {
    if (!previewData) return null;

    const { totalEntries, totalSources, sources, additionalSourceCount } = previewData;
    // Phase B.2 (November 3, 2026): "Found by meaning" when the keyword leg
    // was demoted entirely or any retrieval leg failed; the failure and
    // aggregate-question lines sit under it (copy verbatim).
    const anyLegFailed = previewData.retrievalFailures?.anyFailed === true;
    const foundByMeaning = previewData.keywordLegDemotedEntirely === true || anyLegFailed;
    // `sources` carries at most 10; the remainder beyond those is additionalSourceCount
    // (KRI item 7). Count everything not shown in the top 5.
    const moreSourceCount = sources ? Math.max(0, sources.length - 5) + (additionalSourceCount || 0) : 0;

    return (
      <div className="disclosure-section entries-preview-section">
        <h4 className="disclosure-section-title">Matching Entries</h4>

        <div className="entries-stats">
          <div className="stat-row">
            <span className="stat-label">{foundByMeaning ? 'Found by meaning:' : 'Found:'}</span>
            <span className="stat-value">{totalEntries} {totalEntries === 1 ? 'entry' : 'entries'} across {totalSources} {totalSources === 1 ? 'source' : 'sources'}</span>
          </div>
          {anyLegFailed && (
            <p className="found-note">Part of the search was unavailable; results may be incomplete.</p>
          )}
          {previewData.aggregateQuestion === true && (
            <p className="found-note">This asks for a ranking or count across your whole knowledge base. AI Explorer reads the entries it retrieves and answers for those.</p>
          )}
        </div>

        {sources && sources.length > 0 && (
          <div className="sources-preview">
            <h5>Top Sources:</h5>
            <ul className="sources-preview-list">
              {sources.slice(0, 5).map((source, index) => (
                <li key={index} className="source-preview-item">
                  <span className="source-title">{source.title || source.url}</span>
                  <span className="source-count">{source.entryCount} {source.entryCount === 1 ? 'entry' : 'entries'}</span>
                </li>
              ))}
              {moreSourceCount > 0 && (
                <li className="source-preview-item more">
                  <span className="source-title">+ {moreSourceCount} more {moreSourceCount === 1 ? 'source' : 'sources'}</span>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };

  /**
   * Render the complete left panel content based on flow state
   */
  const renderLeftPanelContent = () => {
    if (flowState === FLOW_STATES.IDLE) {
      return (
        <div className="ai-explorer-placeholder">
          <p>Watch this space to see how Constellation KG helps AIs to
better understand your interests, ideas, and opinions on the topics that
matter to you!
Start by asking a question in the panel to the right...</p>
        </div>
      );
    }

    if (flowState === FLOW_STATES.ERROR) {
      return (
        <div className="disclosure-error">
          <h4>Something went wrong</h4>
          <p>{error}</p>
          <button
            className="retry-button"
            onClick={() => {
              resetState();
              if (currentQuery) {
                setQuery(currentQuery);
              }
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return (
      <div className="ae-tab-container">
        <div className="ae-tab-bar">
          <button
            className={`ae-tab-button ${activeTab === 'progress' ? 'ae-tab-active' : ''}`}
            onClick={() => setActiveTab('progress')}
          >
            Progress
          </button>
          <button
            className={`ae-tab-button ${activeTab === 'map' ? 'ae-tab-active' : ''} ${!graphData ? 'ae-tab-disabled' : ''}`}
            onClick={() => graphData && setActiveTab('map')}
            disabled={!graphData}
          >
            Constellations
          </button>
        </div>

        {activeTab === 'progress' && (
          <div className="ae-tab-panel">
            <div className="progressive-disclosure-panel">
              {currentQuery && (
                <div className="disclosure-query">
                  <h3 className="disclosure-query-text">"{currentQuery}"</h3>
                </div>
              )}

              {previewData && renderSearchedTerms()}
              {previewData && renderEntriesPreview()}

              {isProcessing && (
                <div className="disclosure-processing">
                  <div className="processing-spinner"></div>
                  <span>
                    {flowState === FLOW_STATES.EXTRACTING_TERMS && 'Extracting search terms...'}
                    {flowState === FLOW_STATES.PREVIEWING && 'Searching knowledge base...'}
                    {flowState === FLOW_STATES.ANALYZING && 'AI is analyzing your entries...'}
                  </span>
                </div>
              )}

              {flowState === FLOW_STATES.COMPLETE && response && (
                <div className="disclosure-complete">
                  <div className="complete-checkmark">✓</div>
                  <div className="complete-summary">
                    <strong>Analysis Complete</strong>
                    {/* Sentence text lives in analysisSummary.js, shared with the
                        PDF header (pdfExport.js). Do not inline it here — the two
                        surfaces previously built it separately and contradicted
                        each other on the same run. */}
                    {response.searchMetadata && (() => {
                      const summary = buildAnalysisSummary(response.searchMetadata);
                      return summary ? <p>{summary}</p> : null;
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'map' && (
          <div className="ae-tab-panel">
            <GraphVisualization
              graphData={graphData}
              conceptTags={conceptTags}
            />
          </div>
        )}
      </div>
    );
  };

  // =========================================
  // Main Render
  // =========================================

  return (
    <div className="ai-explorer-page">
      {/* Left side - Progressive Disclosure Panel */}
      <div className="ai-explorer-content">
        {renderLeftPanelContent()}
      </div>

      {/* Right side - AI Query Panel */}
      <div className="ai-explorer-panel">
        <div className="ai-query-section">
          <div className="ai-query-heading-row">
            <h3>Engage Your Knowledge Base</h3>
            {showQueryCounter && (
              <span className={`ai-query-counter ${queriesRemaining <= 1 ? 'ai-query-counter--warn' : ''}`}>
                {queryUsage.used}/{queryUsage.limit} this week
              </span>
            )}
          </div>

          <div className="ai-provider-info">
            <span className="ai-provider-info-label">AI Provider</span>
            <span className="ai-provider-info-value">Anthropic</span>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="query-input-section">
              <div className="query-input-header">
                <label htmlFor="ai-query">What do you want to
know?</label>
                {sttEnabled && (
                  <MicrophoneButton
                    textareaRef={queryTextareaRef}
                    maxLength={null}
                    onTranscriptChange={handleTranscriptChange}
                  />
                )}
              </div>
              <textarea
                id="ai-query"
                ref={queryTextareaRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask a question about your knowledge..."
                rows={2}
                disabled={isProcessing || limitReached}
              />
              {/* THE DICTATION STATUS LINE — full width, below the field.
                  `isRecording` dropped October 13, 2026; see the matching
                  comment in Journal.js. MicrophoneButton no longer renders a
                  status of its own, so this is the only place any dictation
                  message appears. */}
              {sttEnabled && sttStatusMessage && (
                <div
className="stt-status-message">{sttStatusMessage}</div>
              )}
            </div>

            <button
              type="submit"
              className="ai-submit-button"
              disabled={isProcessing || !query.trim() || limitReached}
            >
              {isProcessing ? 'Processing...' : 'Explore'}
            </button>
          </form>

          {error && flowState !== FLOW_STATES.ERROR && (
            <div className="ai-error-display">
              <p>{error}</p>
            </div>
          )}

          {response && (
            <div className="ai-response-display">
              <div className="user-query-heading-row">
                <div className="user-query-heading">{currentQuery}</div>
                {flowState === FLOW_STATES.COMPLETE && (
                  <button
                    className="pdf-export-btn"
                    onClick={() => {
                      const processedText = preprocessMarkdown(stripSourcesSection(response.text));
                      const sections = parseMarkdownSections(processedText);
                      generateAnalysisPDF({
                        query: currentQuery,
                        conceptTags,
                        response,
                        sections,
                        citedEntries: response.citedEntries || [],
                        timestamp: new Date(),
                      });
                    }}
                    title="Download as PDF"
                    aria-label="Download analysis as PDF"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M8 1v9M8 10L5 7M8 10l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 11v2a2 2 0 002 2h8a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    PDF
                  </button>
                )}
              </div>

              {/* Collapsible response sections */}
              {renderCollapsibleResponse()}

              {/* Frontend-rendered Sources table */}
              {renderSourcesTable()}
            </div>
          )}
        </div>

        {/* Limit reached overlay */}
        {limitReached && !hasSubscriberAccess && (
          <div className="ai-limit-overlay">
            <div className="ai-limit-overlay-content">
              <h4>Weekly Query Limit Reached</h4>
              <p>
                You&apos;ve used all {queryUsage.limit} queries for this week.
                {queryUsage.resetsAt && (
                  <> Resets {new Date(queryUsage.resetsAt).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}.</>
                )}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Warning toast */}
      {toastMessage && (
        <div className="ai-toast">
          <span>{toastMessage}</span>
          <button className="ai-toast-dismiss" onClick={() => setToastMessage(null)}>&times;</button>
        </div>
      )}
    </div>
  );
};

export default AIExplorerPage;
